import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import type { DBInvoice } from "@/lib/db-types";
import { formatNOK, formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import { CollapsibleCard } from "@/components/shared/CollapsibleCard";
import {
  useUpdateInvoice,
  fetchInvoicePdfBlob,
  type InvoiceType,
} from "@/hooks/use-invoice-mutations";
import { usePermissions } from "@/hooks/use-permissions";
import { useCustomers } from "@/hooks/use-customers";
import { creditedInvoiceIds as computeCreditedInvoiceIds, effectiveInvoiceType } from "@/lib/invoice-plan";
import { useProjectPermission } from "@/hooks/use-project-permission";
import { describeSendFailure, parsePoSendError } from "@/lib/invoice-send";

const INVOICE_STATUS_COLOR: Record<string, string> = {
  draft: "bg-gray-100 text-gray-600",
  sent: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-700",
  overdue: "bg-red-100 text-red-700",
  cancelled: "bg-gray-200 text-gray-700",
};

// Fakturatype → badge-label + farge. Holder TYPE-kolonnen som ensartede
// badges i stedet for rå tekst.
const INVOICE_TYPE_BADGE: Record<string, { label: string; className: string }> = {
  startup: { label: "Oppstart", className: "bg-indigo-100 text-indigo-700" },
  interim: { label: "Mellomfaktura", className: "bg-sky-100 text-sky-700" },
  final: { label: "Sluttfaktura", className: "bg-emerald-100 text-emerald-700" },
  upsell: { label: "Mersalg", className: "bg-violet-100 text-violet-700" },
};

const BADGE_BASE = "inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium";

/** Klartekst for «Ikke sendt»-merket: hva PO sa og hva som må gjøres. */
function sendFailureTitle(lastSendError: string): string {
  const text = describeSendFailure(parsePoSendError(lastSendError));
  return `${text.title}. ${text.explanation} ${text.action}`;
}

interface InvoicePrefill {
  amount?: number;
  description?: string;
  type?: string;
  related_invoice_id?: string;
}

interface InvoicesListProps {
  projectId: string;
  invoices: DBInvoice[];
  /** VIB-3030: prosjektets hovedkunde. Fakturaer uten egen kunde gikk hit. */
  mainCustomer?: { id: string; name: string } | null;
  showCreateInvoice: boolean;
  onOpenCreate: (prefill?: InvoicePrefill) => void;
}

/**
 * Fakturalisten på prosjektets Faktura-tab: tabell med invoice_no, type
 * (dropdown for superadmin), beskrivelse, beløp (eks/inkl mva), status,
 * datoer, og action-knapper (åpne PDF + lag kreditnota).
 */
export function InvoicesList({
  projectId,
  invoices,
  mainCustomer = null,
  showCreateInvoice,
  onOpenCreate,
}: InvoicesListProps) {
  const { data: customers = [] } = useCustomers();
  const customerName = (inv: DBInvoice) =>
    inv.customer_id && inv.customer_id !== mainCustomer?.id
      ? customers.find((c) => c.id === inv.customer_id)?.name ?? inv.customer_name ?? "—"
      : mainCustomer?.name ?? inv.customer_name ?? "—";
  const { isSuperadmin } = usePermissions();
  // VIB-722c: create_invoice gates "Opprett faktura"-knappen i headeren.
  const canCreateInvoice = useProjectPermission(projectId, "create_invoice");
  const updateInvoiceMut = useUpdateInvoice();
  const [openingPdfId, setOpeningPdfId] = useState<string | null>(null);

  // Sorter nyeste først. invoice_date er primær, created_at fallback når
  // PO-importerte rader mangler eksplisitt fakturadato.
  const sortedInvoices = [...invoices].sort((a, b) => {
    const ad = a.invoice_date ?? a.created_at ?? "";
    const bd = b.invoice_date ?? b.created_at ?? "";
    return bd.localeCompare(ad);
  });

  // Fakturaer som er kreditert fullt ut (samme regel som faktureringsplanen).
  // Brukes til «Kreditert»-merket og til å vise hvilken faktura en kreditnota
  // gjelder.
  const invoiceNoById = new Map(invoices.map((inv) => [inv.id, inv.invoice_no]));
  const creditedInvoiceIds = computeCreditedInvoiceIds(invoices);

  const handleOpenPdf = async (invoiceId: string) => {
    setOpeningPdfId(invoiceId);
    try {
      const { blobUrl } = await fetchInvoicePdfBlob(invoiceId);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setOpeningPdfId(null);
    }
  };

  return (
    <CollapsibleCard
      title="Fakturaer"
      icon={<SrIcon name="banknote" className="w-5 h-5 text-primary" />}
      count={invoices.length > 0 ? invoices.length : undefined}
      actions={(open) =>
        open && canCreateInvoice ? (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => onOpenCreate()}
            disabled={showCreateInvoice}
          >
            <SrIcon name="plus" className="w-3 h-3 mr-1" /> Opprett faktura
          </Button>
        ) : null
      }
      contentClassName="border-t"
    >
      {invoices.length === 0 ? (
        <p className="text-sm text-muted-foreground p-4">
          Ingen fakturaer koblet til dette prosjektet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Nr</th>
                <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Type</th>
                <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Kunde</th>
                <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Beskrivelse</th>
                <th className="text-right py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Eks. mva</th>
                <th className="text-right py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Inkl. mva</th>
                <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Status</th>
                <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Dato</th>
                <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Forfall</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {sortedInvoices.map((inv) => {
                // Kreditnotater har invoice_type=null fra PO; vises som
                // statisk "Kreditnota"-badge (ingen dropdown — type-
                // overstyring gir ikke mening for kreditter).
                const isCreditNote = inv.status === "credit_note";
                // VIB-792: manuelle inntektsrader (ingen PO-faktura) — vis
                // «Manuell»-badge og hold dem helt utenfor PO-flyten
                // (type-dropdown, PDF og kreditnota gjelder ikke).
                const isManual = inv.is_manual === true;
                // VIB-2836: eldre terminfakturaer (upsell + milestone_index) vises som mellomfaktura.
                const invoiceType = effectiveInvoiceType(inv);
                return (
                  <tr
                    key={inv.id}
                    className="border-b border-border/50 last:border-0 hover:bg-muted/30 group"
                  >
                    <td className="py-2.5 px-4 tabular-nums text-muted-foreground">
                      {inv.invoice_no ? `#${inv.invoice_no}` : "—"}
                    </td>
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-1.5">
                        {isManual ? (
                          <span
                            className={cn(BADGE_BASE, "bg-amber-100 text-amber-800")}
                            title="Manuelt registrert inntekt uten PO-faktura (VIB-792)"
                          >
                            Manuell
                          </span>
                        ) : isCreditNote ? (
                          <>
                            <span className={cn(BADGE_BASE, "bg-[#FFE1DB] text-[#7F1D1D]")}>
                              Kreditnota
                            </span>
                            {inv.related_invoice_id &&
                              invoiceNoById.get(inv.related_invoice_id) && (
                                <span className="text-[11px] text-muted-foreground">
                                  → #{invoiceNoById.get(inv.related_invoice_id)}
                                </span>
                              )}
                          </>
                        ) : isSuperadmin ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              {invoiceType ? (
                                <button
                                  type="button"
                                  className={cn(
                                    BADGE_BASE,
                                    "gap-1 hover:opacity-80",
                                    INVOICE_TYPE_BADGE[invoiceType]?.className ??
                                      "border border-border text-muted-foreground",
                                  )}
                                  title="Endre type"
                                >
                                  {INVOICE_TYPE_BADGE[invoiceType]?.label ?? invoiceType}
                                  <SrIcon name="chevron-down" className="w-3 h-3" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-foreground"
                                  title="Sett type"
                                >
                                  <SrIcon name="plus" className="w-4 h-4" />
                                </button>
                              )}
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-40">
                              {(["startup", "interim", "final", "upsell"] as InvoiceType[]).map((t) => (
                                <DropdownMenuItem
                                  key={t}
                                  onClick={() =>
                                    updateInvoiceMut.mutate({
                                      invoice_id: inv.id,
                                      project_id: projectId,
                                      patch: { invoice_type: t },
                                    })
                                  }
                                  className={cn(
                                    "text-xs",
                                    invoiceType === t && "font-semibold",
                                  )}
                                >
                                  {INVOICE_TYPE_BADGE[t].label}
                                </DropdownMenuItem>
                              ))}
                              <DropdownMenuItem
                                onClick={() =>
                                  updateInvoiceMut.mutate({
                                    invoice_id: inv.id,
                                    project_id: projectId,
                                    patch: { invoice_type: null },
                                  })
                                }
                                className="text-xs text-muted-foreground"
                              >
                                <SrIcon name="x" className="w-3 h-3 mr-1.5" />
                                Fjern type
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : invoiceType ? (
                          <span
                            className={cn(
                              BADGE_BASE,
                              INVOICE_TYPE_BADGE[invoiceType]?.className ??
                                "bg-gray-100 text-gray-600",
                            )}
                          >
                            {INVOICE_TYPE_BADGE[invoiceType]?.label ?? invoiceType}
                          </span>
                        ) : null}
                        {creditedInvoiceIds.has(inv.id) && (
                          <span className={cn(BADGE_BASE, "bg-gray-100 text-gray-500")}>
                            Kreditert
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-4">
                      {customerName(inv)}
                      {inv.customer_id && mainCustomer && inv.customer_id !== mainCustomer.id && (
                        <span className="ml-1.5 align-[1px] text-[11px] font-semibold text-primary bg-primary/10 rounded px-1.5 py-px whitespace-nowrap">
                          annen kunde
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-4">{inv.description ?? "—"}</td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-muted-foreground">
                      {inv.amount_excl_vat != null ? formatNOK(inv.amount_excl_vat) : "—"}
                    </td>
                    <td className="py-2.5 px-4 text-right tabular-nums font-medium">
                      {formatNOK(inv.amount)}
                    </td>
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={cn(
                            "px-2 py-0.5 rounded text-[11px] font-medium",
                            INVOICE_STATUS_COLOR[inv.status ?? ""] ?? "bg-gray-100 text-gray-600",
                          )}
                        >
                          {inv.status ?? "—"}
                        </span>
                        {/* Utkast som PO nektet å sende: vis årsaken, ikke bare «draft». */}
                        {inv.status === "draft" && inv.last_send_error && (
                          <span
                            className={cn(BADGE_BASE, "bg-red-100 text-red-700 cursor-help")}
                            title={sendFailureTitle(inv.last_send_error)}
                          >
                            Ikke sendt
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-4 text-muted-foreground">
                      {formatDate(inv.invoice_date ?? inv.created_at)}
                    </td>
                    <td className="py-2.5 px-4 text-muted-foreground">
                      {formatDate(inv.due_date)}
                    </td>
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-1">
                        {inv.poweroffice_invoice_id ? (
                          <button
                            type="button"
                            onClick={() => handleOpenPdf(inv.id)}
                            disabled={openingPdfId === inv.id}
                            className="text-muted-foreground hover:text-primary disabled:opacity-50"
                            title="Åpne faktura-PDF fra PowerOffice"
                            aria-label={`Åpne PDF for faktura ${inv.invoice_no ?? inv.id}`}
                          >
                            {openingPdfId === inv.id ? (
                              <SrIcon name="loader2" className="w-4 h-4 animate-spin" />
                            ) : (
                              <SrIcon name="file-text" className="w-4 h-4" />
                            )}
                          </button>
                        ) : (
                          <span className="opacity-30" title="Ingen PDF tilgjengelig">
                            <SrIcon name="file-text" className="w-4 h-4 text-muted-foreground" />
                          </span>
                        )}
                        {inv.status === "sent" && !isCreditNote && (
                          <button
                            type="button"
                            onClick={() =>
                              onOpenCreate({
                                type: "credit_note",
                                related_invoice_id: inv.id,
                                amount: Number(inv.amount_excl_vat ?? inv.amount ?? 0),
                                description: `Kreditering av faktura #${inv.invoice_no ?? ""}`.trim(),
                              })
                            }
                            className="text-muted-foreground hover:text-destructive"
                            title="Lag kreditnota for denne fakturaen"
                          >
                            <SrIcon name="rotate-ccw" className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CollapsibleCard>
  );
}
